import React, { useState, useEffect, useCallback } from "react"
import {
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import { SharedFilterValues } from "./SharedFilters"
import ExportButton from "./ExportButton"
import reportsService from "../../services/reportsService"
import { formatCentsWithSymbol } from "../../utils/formatCurrency"
import { useTranslation } from "react-i18next"

interface VATBreakdownTabProps {
    filters: SharedFilterValues
    companyId: string
}

interface VATEntry {
    vat_rate_id: string
    vat_rate_percentage: number
    net_amount: number
    vat_amount: number
    gross_amount: number
    transaction_count: number
    currency: string
}

interface VATSection {
    entries: VATEntry[]
    totals: {
        net_total: number
        vat_total: number
        gross_total: number
    }
}

interface VATData {
    sections?: {
        caisse: VATSection
        loca: VATSection | null
    }
    totals?: { net_total: number; vat_total: number; gross_total: number }
    // Legacy shape fallback
    breakdown?: VATEntry[]
}

const fmtC = (v: number, c: string = "EUR") => formatCentsWithSymbol(v, c)

const VATBreakdownTab: React.FC<VATBreakdownTabProps> = ({
    filters,
    companyId,
}) => {
    const { t } = useTranslation()
    const [data, setData] = useState<VATData | null>(null)
    const [loading, setLoading] = useState(false)

    const loadData = useCallback(async () => {
        if (!companyId) return
        setLoading(true)
        try {
            const result = await reportsService.getVATBreakdown(companyId, {
                outletId: filters.outletId || undefined,
                registerId: filters.registerId || undefined,
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
            })
            setData(result as VATData)
        } catch (err) {
            console.error("Failed to load VAT breakdown:", err)
        } finally {
            setLoading(false)
        }
    }, [companyId, filters])

    useEffect(() => {
        loadData()
    }, [loadData])

    const caisse: VATSection = data?.sections?.caisse || {
        entries: data?.breakdown || [],
        totals: (data?.totals as any) || {
            net_total: 0,
            vat_total: 0,
            gross_total: 0,
        },
    }
    const loca: VATSection | null = data?.sections?.loca || null
    const totals = data?.totals || {
        net_total: 0,
        vat_total: 0,
        gross_total: 0,
    }

    const hasAnyData =
        (caisse.entries?.length || 0) > 0 || (loca?.entries?.length || 0) > 0

    const renderSection = (title: string, section: VATSection) => (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                {title}
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                {t(
                                    "exportsComponents.vatBreakdownTab.columns.vatRate"
                                )}
                            </TableCell>
                            <TableCell align="right">
                                {t(
                                    "exportsComponents.vatBreakdownTab.columns.ratePercent"
                                )}
                            </TableCell>
                            <TableCell align="right">
                                {t("exportsComponents.common.ht")}
                            </TableCell>
                            <TableCell align="right">
                                {t("exportsComponents.common.vat")}
                            </TableCell>
                            <TableCell align="right">
                                {t("exportsComponents.common.ttc")}
                            </TableCell>
                            <TableCell align="right">
                                {t(
                                    "exportsComponents.vatBreakdownTab.columns.transactions"
                                )}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(section.entries || []).map((row, i) => (
                            <TableRow key={i} hover>
                                <TableCell>{row.vat_rate_id}</TableCell>
                                <TableCell align="right">
                                    {row.vat_rate_percentage}%
                                </TableCell>
                                <TableCell align="right">
                                    {fmtC(row.net_amount || 0, row.currency)}
                                </TableCell>
                                <TableCell align="right">
                                    {fmtC(row.vat_amount || 0, row.currency)}
                                </TableCell>
                                <TableCell align="right">
                                    {fmtC(row.gross_amount || 0, row.currency)}
                                </TableCell>
                                <TableCell align="right">
                                    {row.transaction_count}
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow sx={{ "& td": { fontWeight: 600 } }}>
                            <TableCell colSpan={2}>
                                {t(
                                    "exportsComponents.vatBreakdownTab.subtotal"
                                )}
                            </TableCell>
                            <TableCell align="right">
                                {fmtC(section.totals?.net_total || 0)}
                            </TableCell>
                            <TableCell align="right">
                                {fmtC(section.totals?.vat_total || 0)}
                            </TableCell>
                            <TableCell align="right">
                                {fmtC(section.totals?.gross_total || 0)}
                            </TableCell>
                            <TableCell />
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    )

    return (
        <Box>
            {loading ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : hasAnyData ? (
                <>
                    <Paper
                        sx={{
                            p: 2,
                            mb: 2,
                            display: "flex",
                            gap: 3,
                            alignItems: "center",
                            flexWrap: "wrap",
                        }}
                    >
                        <Typography variant="body2">
                            <strong>
                                {t(
                                    "exportsComponents.vatBreakdownTab.sections.grandTotal"
                                )}
                                :
                            </strong>
                        </Typography>
                        <Typography variant="body2">
                            <strong>
                                {t(
                                    "exportsComponents.vatBreakdownTab.summary.totalHt"
                                )}
                                :
                            </strong>{" "}
                            {fmtC(totals.net_total || 0)}
                        </Typography>
                        <Typography variant="body2">
                            <strong>
                                {t(
                                    "exportsComponents.vatBreakdownTab.summary.totalVat"
                                )}
                                :
                            </strong>{" "}
                            {fmtC(totals.vat_total || 0)}
                        </Typography>
                        <Typography variant="body2">
                            <strong>
                                {t(
                                    "exportsComponents.vatBreakdownTab.summary.totalTtc"
                                )}
                                :
                            </strong>{" "}
                            {fmtC(totals.gross_total || 0)}
                        </Typography>
                        <Box sx={{ ml: "auto" }}>
                            <ExportButton
                                companyId={companyId}
                                reportType="vat_breakdown"
                                filters={filters}
                            />
                        </Box>
                    </Paper>
                    {renderSection(
                        t(
                            "exportsComponents.vatBreakdownTab.sections.caisse"
                        ),
                        caisse
                    )}
                    {loca &&
                        renderSection(
                            t(
                                "exportsComponents.vatBreakdownTab.sections.loca"
                            ),
                            loca
                        )}
                </>
            ) : (
                <Box>
                    <Paper
                        sx={{
                            p: 2,
                            mb: 2,
                            display: "flex",
                            justifyContent: "flex-end",
                        }}
                    >
                        <ExportButton
                            companyId={companyId}
                            reportType="vat_breakdown"
                            filters={filters}
                        />
                    </Paper>
                    <Paper sx={{ p: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                            {t("exportsComponents.vatBreakdownTab.empty")}
                        </Typography>
                    </Paper>
                </Box>
            )}
        </Box>
    )
}

export default VATBreakdownTab
